import type { Tone } from "@amtana/design-system";
import type { Catalogs, PlainKey, Translation } from "@amtana/i18n/catalog";
import type { ListStatus } from "@amtana/list-contract";
import type { ConditionField, Operator } from "@amtana/prospect-contract";

// The filter screens' words, in English and Spanish: one key per string, read
// through `useListCopy` (behavior/use-list-copy.ts). The prospect contract
// names each condition field, option and operator in English; the tables below
// hold the keys that read them in the active language.

export type ListCopyKey = keyof typeof EN;
export type ListCatalog = typeof EN;

const EN = {
  listsScreenTitle: "Filters · {product}",
  newScreenTitle: "New Filter · {product}",
  editScreenTitle: "Edit Filter · {product}",
  detailScreenTitle: "Filter · {product}",
  navFilters: "Filters",
  statusBuilding: "Building",
  statusReady: "Ready",
  statusEmpty: "Empty",
  statusError: "Error",
  demoFitness: "Fitness influencers +10K followers",
  demoStores: "Online stores with email",
  demoAgencies: "Marketing agencies",
  demoRestaurants: "Restaurants without a website",
  demoFounders: "SaaS founders +5K followers",
  notBuiltYet: "Not built yet",
  breadcrumbHome: "Home",
  breadcrumbFilters: "Filters",
  breadcrumbEdit: "Edit",
  breadcrumbNew: "New",
  filtersTitle: "Filters",
  filtersDescription: "Manage your filters",
  newFilter: "New Filter",
  searchFilters: "Search filters",
  emptyTitle: "You don't have any filters yet.",
  emptyDescription: "Combine prospects from several analyses into one reusable audience.",
  columnName: "Name",
  columnStatus: "Status",
  columnProspects: "Prospects",
  columnUpdated: "Updated",
  columnActions: "Actions",
  actionsLabel: "Actions for {name}",
  actionView: "View",
  actionEdit: "Edit filters",
  actionRebuild: "Rebuild",
  actionDelete: "Delete",
  rebuildStarted: "Filter rebuild started",
  couldNotRebuild: "Could not rebuild the filter",
  deleted: "Filter deleted",
  couldNotDelete: "Could not delete the filter",
  filterDetails: "Filter details",
  edit: "Edit",
  options: "Options",
  viewExports: "View exports",
  detailsPanel: "Details",
  detailStatus: "Status",
  detailProspects: "Prospects",
  detailWithEmail: "With email",
  detailCreated: "Created",
  detailLastBuilt: "Last built",
  rulesPanel: "Rules",
  sourceAnalysisCount: { one: "1 source analysis", other: "{count} source analyses" },
  everyone: "Everyone in the source analyses.",
  and: "AND",
  editTitle: "Edit Filter",
  newTitle: "New Filter",
  editDescription: "Edit filter conditions and settings",
  newDescription: "Create a new filter",
  cancel: "Cancel",
  save: "Save",
  create: "Create",
  fieldName: "Name",
  namePlaceholder: "Coffee shops with email",
  fieldDescription: "Description",
  descriptionHint: "Optional, up to 500 characters",
  matchesPanel: "Matches",
  matchesProspects: "Prospects",
  matchesWithEmail: "With email",
  sourcesPanel: "Source analyses",
  sourcesDescription: "Select analyses used as data source for this filter. Max 10 analyses.",
  runAnalysisFirst: "Run an analysis first: filters are built from analyses that have prospects.",
  sourceCounts: "{prospects} · {emails}",
  prospectCount: { one: "1 prospect", other: "{count} prospects" },
  emailCount: { one: "1 email", other: "{count} emails" },
  updated: "Filter updated",
  created: "Filter created",
  couldNotUpdate: "Could not update the filter",
  couldNotCreate: "Could not create the filter",
  ruleIntro: "Add one or more rules to define who should be included in the filter. Max {max} rules.",
  addRule: "Add rule",
  removeRule: "Remove rule",
  ruleField: "Field",
  ruleOperator: "Operator",
  ruleValues: "Values",
  selectOptions: "Select options",
  enterValue: "Enter a value",
  categoryProfile: "Profile",
  categoryMetrics: "Social metrics",
  categoryBusiness: "Business",
  categoryContact: "Contact",
  categoryLocation: "Google Maps",
  lockedTitle: "Filters are not available on your plan",
  lockedDescription:
    "Upgrade your plan to create and manage filters: reusable audiences built from several analyses.",
  upgradePlan: "Upgrade plan",
  sampleFilters: "Sample filters",
  conditionBiography: "Biography",
  conditionUsername: "Username",
  conditionCountry: "Country",
  conditionLanguage: "Language",
  conditionNiche: "Niche / Industry",
  conditionGender: "Gender",
  conditionFollowers: "Followers",
  conditionFollowing: "Following",
  conditionAvgLikes: "Avg. Likes",
  conditionAvgComments: "Avg. Comments",
  conditionEngagementRate: "Engagement Rate (%)",
  conditionIsBusiness: "Is business account",
  conditionEntityType: "Person or Business",
  conditionB2bB2c: "B2B / B2C",
  conditionHasEmail: "Has email",
  conditionHasPhone: "Has phone number",
  conditionHasWebsite: "Has website",
  conditionEmail: "Email",
  conditionPhone: "Phone number",
  conditionWebsite: "Website",
  conditionEmailType: "Email type",
  conditionQualification: "Qualification",
  conditionCategory: "Business type",
  conditionAddress: "Address",
  conditionRating: "Rating",
  conditionReviews: "Reviews count",
  optionMale: "Male",
  optionFemale: "Female",
  optionUnknown: "Unknown",
  optionPerson: "Person",
  optionBusiness: "Business",
  optionB2bFull: "B2B (Business-to-Business)",
  optionB2cFull: "B2C (Business-to-Consumer)",
  optionEmailB2b: "B2B (Business)",
  optionEmailB2c: "B2C (Personal)",
  optionInterested: "Interested",
  optionNotInterested: "Not interested",
  optionWrongPerson: "Wrong person",
  optionDoNotContact: "Do not contact",
  optionOutOfOffice: "Out of office",
  operatorEquals: "is equal to",
  operatorNotEquals: "is not equal to",
  operatorContains: "contains",
  operatorNotContains: "does not contain",
  operatorStartsWith: "starts with",
  operatorEndsWith: "ends with",
  operatorGreaterThan: "is greater than",
  operatorGreaterThanOrEqual: "is greater than or equal to",
  operatorLessThan: "is less than",
  operatorLessThanOrEqual: "is less than or equal to",
  operatorIn: "is one of",
  operatorNotIn: "is not one of",
  operatorExists: "exists",
  operatorNotExists: "does not exist",
  operatorIsTrue: "is true",
  operatorIsFalse: "is false",
} as const;

const ES: Translation<ListCatalog> = {
  listsScreenTitle: "Filtros · {product}",
  newScreenTitle: "Nuevo filtro · {product}",
  editScreenTitle: "Editar filtro · {product}",
  detailScreenTitle: "Filtro · {product}",
  navFilters: "Filtros",
  statusBuilding: "Creándose",
  statusReady: "Listo",
  statusEmpty: "Vacío",
  statusError: "Error",
  demoFitness: "Influencers de fitness con +10K seguidores",
  demoStores: "Tiendas en línea con correo",
  demoAgencies: "Agencias de marketing",
  demoRestaurants: "Restaurantes sin sitio web",
  demoFounders: "Fundadores de SaaS con +5K seguidores",
  notBuiltYet: "Aún no se crea",
  breadcrumbHome: "Inicio",
  breadcrumbFilters: "Filtros",
  breadcrumbEdit: "Editar",
  breadcrumbNew: "Nuevo",
  filtersTitle: "Filtros",
  filtersDescription: "Gestiona tus filtros",
  newFilter: "Nuevo filtro",
  searchFilters: "Buscar filtros",
  emptyTitle: "Todavía no tienes filtros.",
  emptyDescription: "Combina prospectos de varios análisis en una audiencia reutilizable.",
  columnName: "Nombre",
  columnStatus: "Estado",
  columnProspects: "Prospectos",
  columnUpdated: "Actualizado",
  columnActions: "Acciones",
  actionsLabel: "Acciones de {name}",
  actionView: "Ver",
  actionEdit: "Editar filtro",
  actionRebuild: "Recrear",
  actionDelete: "Eliminar",
  rebuildStarted: "Se inició la recreación del filtro",
  couldNotRebuild: "No se pudo recrear el filtro",
  deleted: "Filtro eliminado",
  couldNotDelete: "No se pudo eliminar el filtro",
  filterDetails: "Detalles del filtro",
  edit: "Editar",
  options: "Opciones",
  viewExports: "Ver exportaciones",
  detailsPanel: "Detalles",
  detailStatus: "Estado",
  detailProspects: "Prospectos",
  detailWithEmail: "Con correo",
  detailCreated: "Creado",
  detailLastBuilt: "Última creación",
  rulesPanel: "Reglas",
  sourceAnalysisCount: { one: "1 análisis de origen", other: "{count} análisis de origen" },
  everyone: "Todos los de los análisis de origen.",
  and: "Y",
  editTitle: "Editar filtro",
  newTitle: "Nuevo filtro",
  editDescription: "Edita las condiciones y los ajustes del filtro",
  newDescription: "Crea un filtro nuevo",
  cancel: "Cancelar",
  save: "Guardar",
  create: "Crear",
  fieldName: "Nombre",
  namePlaceholder: "Cafeterías con correo",
  fieldDescription: "Descripción",
  descriptionHint: "Opcional, hasta 500 caracteres",
  matchesPanel: "Coincidencias",
  matchesProspects: "Prospectos",
  matchesWithEmail: "Con correo",
  sourcesPanel: "Análisis de origen",
  sourcesDescription:
    "Elige los análisis que sirven de fuente de datos para este filtro. Máximo 10 análisis.",
  runAnalysisFirst: "Primero ejecuta un análisis: los filtros se crean a partir de análisis con prospectos.",
  sourceCounts: "{prospects} · {emails}",
  prospectCount: { one: "1 prospecto", other: "{count} prospectos" },
  emailCount: { one: "1 correo", other: "{count} correos" },
  updated: "Filtro actualizado",
  created: "Filtro creado",
  couldNotUpdate: "No se pudo actualizar el filtro",
  couldNotCreate: "No se pudo crear el filtro",
  ruleIntro: "Agrega una o más reglas para definir quién se incluye en el filtro. Máximo {max} reglas.",
  addRule: "Agregar regla",
  removeRule: "Quitar regla",
  ruleField: "Campo",
  ruleOperator: "Operador",
  ruleValues: "Valores",
  selectOptions: "Elige opciones",
  enterValue: "Escribe un valor",
  categoryProfile: "Perfil",
  categoryMetrics: "Métricas sociales",
  categoryBusiness: "Negocio",
  categoryContact: "Contacto",
  categoryLocation: "Google Maps",
  lockedTitle: "Los filtros no están disponibles en tu plan",
  lockedDescription:
    "Mejora tu plan para crear y gestionar filtros: audiencias reutilizables creadas a partir de varios análisis.",
  upgradePlan: "Mejorar plan",
  sampleFilters: "Filtros de ejemplo",
  conditionBiography: "Biografía",
  conditionUsername: "Usuario",
  conditionCountry: "País",
  conditionLanguage: "Idioma",
  conditionNiche: "Nicho / Industria",
  conditionGender: "Género",
  conditionFollowers: "Seguidores",
  conditionFollowing: "Seguidos",
  conditionAvgLikes: "Prom. de me gusta",
  conditionAvgComments: "Prom. de comentarios",
  conditionEngagementRate: "Tasa de interacción (%)",
  conditionIsBusiness: "Es cuenta de negocio",
  conditionEntityType: "Persona o negocio",
  conditionB2bB2c: "B2B / B2C",
  conditionHasEmail: "Tiene correo",
  conditionHasPhone: "Tiene teléfono",
  conditionHasWebsite: "Tiene sitio web",
  conditionEmail: "Correo",
  conditionPhone: "Teléfono",
  conditionWebsite: "Sitio web",
  conditionEmailType: "Tipo de correo",
  conditionQualification: "Calificación",
  conditionCategory: "Tipo de negocio",
  conditionAddress: "Dirección",
  conditionRating: "Valoración",
  conditionReviews: "Cantidad de reseñas",
  optionMale: "Masculino",
  optionFemale: "Femenino",
  optionUnknown: "Desconocido",
  optionPerson: "Persona",
  optionBusiness: "Negocio",
  optionB2bFull: "B2B (de negocio a negocio)",
  optionB2cFull: "B2C (de negocio a consumidor)",
  optionEmailB2b: "B2B (de negocio)",
  optionEmailB2c: "B2C (personal)",
  optionInterested: "Interesado",
  optionNotInterested: "No interesado",
  optionWrongPerson: "Persona equivocada",
  optionDoNotContact: "No contactar",
  optionOutOfOffice: "Fuera de la oficina",
  operatorEquals: "es igual a",
  operatorNotEquals: "no es igual a",
  operatorContains: "contiene",
  operatorNotContains: "no contiene",
  operatorStartsWith: "empieza con",
  operatorEndsWith: "termina con",
  operatorGreaterThan: "es mayor que",
  operatorGreaterThanOrEqual: "es mayor o igual que",
  operatorLessThan: "es menor que",
  operatorLessThanOrEqual: "es menor o igual que",
  operatorIn: "es uno de",
  operatorNotIn: "no es uno de",
  operatorExists: "existe",
  operatorNotExists: "no existe",
  operatorIsTrue: "es verdadero",
  operatorIsFalse: "es falso",
};

export const LIST_CATALOGS: Catalogs<ListCatalog> = { en: EN, es: ES };

type ListKey = PlainKey<ListCatalog>;

export const LIST_STATUS: Readonly<Record<ListStatus, { label: ListKey; tone: Tone }>> = {
  building: { label: "statusBuilding", tone: "info" },
  ready: { label: "statusReady", tone: "success" },
  empty: { label: "statusEmpty", tone: "neutral" },
  error: { label: "statusError", tone: "danger" },
};

/** The rows the locked screen shows on plans without filters. */
export const DEMO_LISTS: ReadonlyArray<{ name: ListKey; prospects: number }> = [
  { name: "demoFitness", prospects: 1_240 },
  { name: "demoStores", prospects: 3_812 },
  { name: "demoAgencies", prospects: 674 },
  { name: "demoRestaurants", prospects: 2_105 },
  { name: "demoFounders", prospects: 418 },
];

export const CATEGORY_LABEL = {
  profile: "categoryProfile",
  metrics: "categoryMetrics",
  business: "categoryBusiness",
  contact: "categoryContact",
  location: "categoryLocation",
} as const satisfies Readonly<Record<string, ListKey>>;

export const CONDITION_LABEL: Readonly<Record<ConditionField, ListKey>> = {
  biography: "conditionBiography",
  username: "conditionUsername",
  country: "conditionCountry",
  language: "conditionLanguage",
  niche: "conditionNiche",
  gender: "conditionGender",
  followers: "conditionFollowers",
  following: "conditionFollowing",
  avgLikes: "conditionAvgLikes",
  avgComments: "conditionAvgComments",
  engagementRate: "conditionEngagementRate",
  isBusiness: "conditionIsBusiness",
  entityType: "conditionEntityType",
  b2bB2c: "conditionB2bB2c",
  hasEmail: "conditionHasEmail",
  hasPhone: "conditionHasPhone",
  hasWebsite: "conditionHasWebsite",
  email: "conditionEmail",
  phone: "conditionPhone",
  website: "conditionWebsite",
  emailType: "conditionEmailType",
  qualification: "conditionQualification",
  category: "conditionCategory",
  address: "conditionAddress",
  rating: "conditionRating",
  reviews: "conditionReviews",
};

/** The choices of an enum field, by field then value; a value missing here reads as the contract names it. */
export const OPTION_LABEL: Readonly<Partial<Record<ConditionField, Readonly<Record<string, ListKey>>>>> = {
  gender: { male: "optionMale", female: "optionFemale", unknown: "optionUnknown" },
  entityType: { person: "optionPerson", business: "optionBusiness" },
  b2bB2c: { b2b: "optionB2bFull", b2c: "optionB2cFull", unknown: "optionUnknown" },
  emailType: { b2b: "optionEmailB2b", b2c: "optionEmailB2c" },
  qualification: {
    interested: "optionInterested",
    "not-interested": "optionNotInterested",
    "wrong-person": "optionWrongPerson",
    "do-not-contact": "optionDoNotContact",
    "out-of-office": "optionOutOfOffice",
  },
};

export const OPERATOR_LABEL: Readonly<Record<Operator, ListKey>> = {
  equals: "operatorEquals",
  notEquals: "operatorNotEquals",
  contains: "operatorContains",
  notContains: "operatorNotContains",
  startsWith: "operatorStartsWith",
  endsWith: "operatorEndsWith",
  greaterThan: "operatorGreaterThan",
  greaterThanOrEqual: "operatorGreaterThanOrEqual",
  lessThan: "operatorLessThan",
  lessThanOrEqual: "operatorLessThanOrEqual",
  in: "operatorIn",
  notIn: "operatorNotIn",
  exists: "operatorExists",
  notExists: "operatorNotExists",
  isTrue: "operatorIsTrue",
  isFalse: "operatorIsFalse",
};
